import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { onLogin, onLogout, onSessionLost } from "../../redux/authSlice";
import store from "../../redux/store";
import { useConfiguration } from "./useConfiguration";
import { AuthService } from '../../services/AuthService';

export const useAuthentication = () => {
    const isSessionLost = useSelector((state) => state.auth.isSessionLost);
    const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
    const authenticatedUser = useSelector(state => state.auth.authenticatedUser);

    const { appConfig } = useConfiguration();

    const authService = new AuthService(appConfig.apiBaseUrl);

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const redirectToLoginPage = (redirectBackAfterLogin = true) => {
        const basePath = process.env.PUBLIC_URL || './';
        const pathname = window.location.pathname.startsWith(basePath)
            ? window.location.pathname.slice(basePath.length)
            : window.location.pathname;

        const redirectUrl = `${pathname}${window.location.search}`;

        redirectBackAfterLogin && sessionStorage.setItem('redirectAfterLogin', redirectUrl);

        // Find /admin in the pathname and redirect to .../admin/login
        // This works for both scenarios:
        // - Path-based: /properties/1/admin -> /properties/1/admin/login
        // - Domain-based: /admin -> /admin/login
        const adminIndex = pathname.indexOf('/admin');
        if (adminIndex !== -1) {
            // Extract everything up to and including /admin, then append /login
            const adminPath = pathname.substring(0, adminIndex + '/admin'.length);
            navigate(`${adminPath}/login`);
        } else {
            // Fallback: redirect to root path if /admin is not found
            navigate(`/`);
        }
    }

    const logout = async () => {
        await authService.logout();
        dispatch(onLogout());
        redirectToLoginPage(false);
    }

    const login = async (credentials) => {
        const authenticatedUser = await authService.login(credentials);
        dispatch(onLogin(authenticatedUser));
    }

    const reauthenticate = async () => {
        dispatch(onSessionLost());

        return new Promise((resolve) => {
            const unsubscribe = store.subscribe(() => {
                const sessionRestored = !store.getState().auth.isSessionLost;
                if (sessionRestored) {
                    unsubscribe();
                    resolve();
                }
            });
        });
    }

    const validateSession = async () => {
        const authenticatedUser = await authService.validateSession();
        dispatch(onLogin(authenticatedUser));
    }

    return { login, logout, redirectToLoginPage, validateSession, isAuthenticated, isSessionLost, reauthenticate, authenticatedUser };
}